import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/corrections")({
  head: () => ({
    meta: [
      { title: "Corrections | Acadly.in" },
      {
        name: "description",
        content:
          "How Acadly.in handles corrections, updates, and reader feedback. Report an error or request a clarification.",
      },
      { property: "og:title", content: "Corrections | Acadly.in" },
      {
        property: "og:description",
        content:
          "Acadly.in's corrections policy and a running log of corrected articles.",
      },
      { property: "og:url", content: "https://acadly.in/corrections" },
    ],
    links: [{ rel: "canonical", href: "https://acadly.in/corrections" }],
  }),
  component: CorrectionsPage,
});

function CorrectionsPage() {
  return (
    <div className="mx-auto max-w-3xl px-5 py-16 md:py-20 prose-post">
      <div className="stamp mb-6">Corrections</div>
      <h1
        className="text-5xl md:text-6xl leading-[1] tracking-tight font-semibold"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Corrections
      </h1>
      <p className="mono-label !mt-2">Last updated: 25 July 2026</p>

      <p>
        We try to get every fact, formula, date, and policy detail right. When
        we do not, we fix it quickly and tell readers what changed. This page
        explains how to report a mistake, how we handle corrections, and a log
        of articles that have been updated.
      </p>

      <h2>How to report an error</h2>
      <p>
        If you spot a factual error, an outdated rule, a broken calculation, or a
        misleading source, please send a note. Include the article title, the
        sentence or section you believe is wrong, and a source or explanation if
        you have one.
      </p>
      <p>
        Email: <a href="mailto:hello@acadly.in">hello@acadly.in</a>
        <br />
        Alternatively, use the <Link to="/contact">Contact page</Link>.
      </p>
      <p>
        We read every correction request. Substantive corrections are usually
        reviewed within 48 hours, and straightforward factual fixes are published
        as soon as they are verified.
      </p>

      <h2>What counts as a correction</h2>
      <ul>
        <li>Incorrect dates, deadlines, names, or figures.</li>
        <li>
          Outdated policy references that no longer match the official source.
        </li>
        <li>Wrong formulas or worked-example calculations.</li>
        <li>Broken links or missing citations that change a claim.</li>
        <li>Any statement that misrepresents a source.</li>
      </ul>
      <p>
        Typographical errors, minor grammar fixes, and style updates are made
        without a formal correction note unless they affect meaning.
      </p>

      <h2>How we mark corrections</h2>
      <p>
        When a published article is corrected, we add a dated note at the bottom
        of the article under the heading "Correction" or "Update". The note
        states:
      </p>
      <ul>
        <li>What was changed.</li>
        <li>Why it was changed.</li>
        <li>The date of the correction.</li>
      </ul>
      <p>
        We do not silently edit articles in a way that removes a previously
        published error. If the error is significant, the correction note remains
        visible permanently.
      </p>

      <h2>Updates versus corrections</h2>
      <p>
        An update is a routine change made because the underlying facts have
        evolved. Examples include a new admission schedule, a revised syllabus,
        or a fresh policy announcement. An update is marked with a dated "Update"
        note. A correction is a fix for an error we made. The distinction is
        small, but it matters for transparency.
      </p>

      <h2>Corrections log</h2>
      <p>
        No formal corrections have been published yet. This log will be updated
        whenever a correction note is added to an article.
      </p>
      <div className="border border-rule rounded-sm bg-cream p-5 mt-6">
        <p className="mono-label mb-2">Published corrections</p>
        <p className="text-sm text-pencil">None at this time.</p>
      </div>

      <h2>Requests for removal or takedown</h2>
      <p>
        Acadly.in publishes factual study and news content. We do not remove
        articles simply because a subject dislikes them. If you believe an
        article contains a serious inaccuracy, the correct route is to request a
        correction. We will review the evidence and either correct the article or
        explain why we stand by the original reporting.
      </p>

      <h2>Contact</h2>
      <p>
        For corrections, source requests, or editorial feedback, write to{" "}
        <a href="mailto:hello@acadly.in">hello@acadly.in</a>. For general
        questions, visit the <Link to="/contact">Contact page</Link>.
      </p>
    </div>
  );
}
